/* eslint-disable max-lines */
import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import Logo from '@comp/Logo/Logo';
import HeaderTop from '@layout/Components/HeaderContainer';
import HeaderContent from '@layout/Components/HeaderParent';
import { mediaQuery, Spacings } from '@styles/index';
import { useToggleable } from '@stores/toggleables';
import { LOGIN_DRAWER } from '@constants/toggleables';
import Dynamic from '@base/Dynamic/Dynamic';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn } from '@api/selectors/auth';
import { Tabs, TabList, Tab } from 'aquilla/core/molecules/Tabs';
import { FREE_HOME_PAGE, HOME_NAVIGATION_CLICKED } from '@constants/segment';
import { TAB_STORE_CLICKED } from '@cont/StoreListing/constants';
import useGoalEventProps from '@hooks/useGoalEventProps';
import ReferralNudge from '@comp/ReferralNudge/ReferralNudge';
import { segment } from '@actions/analytics';
import {
  GOAL_HOME,
  SUBSCRIPTION,
  SUCCESS_STORY,
  TABSID,
  ABOUT_TAB,
  HEADER_TABS,
  STORE_TAB,
  STORE
} from '@cont/NewPreSubscribed/constants';
import pushRoute from '@utils/pushRoute';
import { useRouter } from 'next/router';
import useSeoContent from '@api/hooks/useSeoContent';
import useFirstRenderSkip from '@hooks/useFirstRenderSkip';
import Typography from 'aquilla/core/atoms/Typography';
import Image from '@base/Image';
import LoginComponent from '@cont/Login/LoginComponent';
import useLoginModalExperiment from '@cont/Login/utils/hooks/useLoginModalExperiment';
import useWindowSize from '@hooks/useWindowSize';
import useStoreNudge from '@cont/Store/useStoreNudge';
import useUserProfileInfo from '@api/hooks/goal/useUserProfileInfo';
import HeaderUser from './HeaderUser/HeaderUser';
import NewSearchTab from './NewSearchTab';
import RenderLoginAction from './RenderLoginAction';

const GoalSelector = Dynamic(() => import('./GoalSelector/HeaderGoalSelector'));
const PaymentNudge = Dynamic(() =>
  import('@layout/PaymentLayout/PaymentNudge')
);
const StoreTooltip = Dynamic(() => import('@cont/Store/StoreTooltip'));

const HeaderContainer = styled(HeaderTop)`
  z-index: 3;
  background: ${({ transparent, hideBgColor }) =>
    transparent || hideBgColor ? 'none' : 'var(--color-base-1)'};
  border-bottom: 1px solid #e9eef2;
  position: ${({ transparent }) => (transparent ? 'absolute' : 'sticky')};
  ${mediaQuery.uptoTablet} {
    height: auto;
  }
  ${mediaQuery.uptoMobile} {
    display: block;
    width: 100%;
    background-color: var(--color-base-1);
    margin-bottom: 0px;
  }
  height: 64px;
  margin-bottom: 57px;
`;

const LogoWrapper = styled.div`
  [class*='LogoContainer'] {
    display: flex;
  }
  ${mediaQuery.uptoMobile} {
    margin-right: ${Spacings.SPACING_2B};
  }
`;

const SearchWrapper = styled.div`
  flex: 1;
  ${mediaQuery.uptoTablet} {
    display: none;
  }
`;

const RightContentWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-basis: 264px;
  justify-content: flex-end;
  grid-column: 2/3;
  [aria-label='Login'] {
    background-color: var(--color-base-fill);

    ${mediaQuery.uptoMobile} {
      color: var(--color-text-tertiary);
      background-color: var(--color-base-fill);
      width: 72px;
    }
  }
`;

const HeaderUserContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const GoalSelectorWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-basis: 232px;
  ${mediaQuery.uptoMobile} {
    max-width: 90px;
    flex: 1;
    justify-content: flex-start;
    margin-right: ${Spacings.SPACING_2B};
    button {
      padding-left: 0px;
    }
  }
`;

const TabWrapper = styled.div`
  height: 100%;
  .aquilla-tablist {
    justify-content: flex-start;
  }

  ${mediaQuery.uptoTablet} {
    padding: 0 ${Spacings.SPACING_8B};
    .aquilla-tablist {
      justify-content: flex-start;
    }
  }

  ${mediaQuery.uptoMobile} {
    padding: 0 ${Spacings.SPACING_4B};
  }

  > * {
    height: 100%;
  }
`;

const LoginProfileWrapper = styled.div``;

const HeaderParent = styled(HeaderContent)`
  position: relative;
  justify-content: space-between;
  display: grid;
  grid-template-columns: 260px 1fr;
  ${mediaQuery.uptoTablet} {
    width: 100%;
    display: flex;
    padding: ${Spacings.SPACING_4B} ${Spacings.SPACING_8B} 0
      ${Spacings.SPACING_8B};
  }
  ${mediaQuery.uptoMobile} {
    height: 56px;
    display: flex;
    width: 100%;
    padding: 16px;
    justify-content: space-between;
  }

  ${GoalSelectorWrapper},
  ${LoginProfileWrapper},
    ${LogoWrapper},
    ${TabWrapper} {
    transition: all linear 300ms;
  }
`;

const LogoGoalSelectorWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-basis: 264px;
  ${mediaQuery.uptoTablet} {
    gap: 0;
  }
`;

const ScreenWrapper = styled.div`
  height: 100%;
  &.desktop {
    display: block;
    ${mediaQuery.uptoTablet} {
      display: none;
    }
  }
  &.mobile {
    display: none;
    ${mediaQuery.uptoTablet} {
      display: block;
    }
  }
`;
const TabSubContainer = styled.div`
  width: 1136px;
  margin: 0 auto;
  .tablist {
    min-height: 32px;
  }
  .aquilla-tab {
    min-height: 32px;
    height: 32px;
  }
  ${mediaQuery.uptoMobile} {
    .tablist {
      min-height: 40px;
    }
    .aquilla-tab {
      min-height: 40px;
      height: 40px;
    }
  }
`;
const TabContainer = styled.div`
  width: 100%;
  background: #ffffff;
  position: relative;
  z-index: 2;
  border-bottom: ${Spacings.SPACING_1} solid #e9eef2;
  ${mediaQuery.uptoMobile} {
    display: none;
  }
`;

const TabLabel = styled.div`
  display: flex;
  gap: ${Spacings.SPACING_1B};
  align-items: center;
`;

const ContactWrapper = styled.div`
  display: flex;
  padding: ${Spacings.SPACING_0B} ${Spacings.SPACING_2B};
  ${mediaQuery.uptoTablet} {
    display: none;
  }
`;

const PhoneWrapper = styled.div`
  text-align: center;
`;

const Phone = styled.a`
  display: flex;
  width: fit-content;
`;

const IconWrapper = styled.a`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const RenderTabs = ({
  handleOnChange,
  currentTabIndex,
  showStoreTooltip,
  headerTabs
}) => (
  <TabWrapper>
    <Tabs onChange={handleOnChange} index={currentTabIndex}>
      <TabList>
        {headerTabs.map((tab) => {
          const tabElement = (
            <Tab key={tab.id} id={tab.id}>
              <TabLabel>{tab.label}</TabLabel>
            </Tab>
          );
          if (tab.id === STORE_TAB.id && showStoreTooltip)
            return <StoreTooltip>{tabElement}</StoreTooltip>;
          return tabElement;
        })}
      </TabList>
    </Tabs>
  </TabWrapper>
);
const ContactUs = () => {
  return (
    <ContactWrapper>
      <IconWrapper href="tel:918585858585">
        <Image src="goal/callUs.svg" width="40" height="40" alt="call-us" />
      </IconWrapper>
      <PhoneWrapper>
        <Typography variant="p3" color="var(--color-text-secondary)">
          Talk to our experts
        </Typography>
        <Phone href="tel:918585858585">
          <Typography variant="h6">+91-8585858585</Typography>
        </Phone>
      </PhoneWrapper>
    </ContactWrapper>
  );
};

const NewGoalHeader = ({ transparent, isReload, disabled, pageMetaInfo }) => {
  const { isOpen: showLoginDrawer, closeElement: closeLoginDrawer } =
    useToggleable(LOGIN_DRAWER);
  const {
    query: { goalUID, goalSlug },
    pathname
  } = useRouter();
  useLoginModalExperiment();
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const goalSegmentData = useGoalEventProps(goalUID);
  const { userProfileInfo: storePurchaseData } = useUserProfileInfo(goalUID);
  let pageName = GOAL_HOME;
  if (pathname.includes('subscriptions')) pageName = SUBSCRIPTION;
  else if (pathname.includes('success-story')) pageName = SUCCESS_STORY;
  else if (pathname.includes('store')) pageName = STORE;
  const [currentTabIndex, setTabIndex] = useState();
  const [showStoreTab, setShowStoreTab] = useState(pageMetaInfo?.showStoreTab);

  const { seoContent } = useSeoContent(FREE_HOME_PAGE, goalUID);
  const { title: aboutSecTitle } =
    pageMetaInfo?.contentBlock || seoContent?.contentBlock || {};
  const allTabs = [...HEADER_TABS];

  if (aboutSecTitle) {
    allTabs.push(ABOUT_TAB);
  }
  if (showStoreTab) {
    allTabs.splice(3, 0, STORE_TAB);
  }

  const [headerTabs, setHeaderTabs] = useState(allTabs);
  const { isTablet, isMobile } = useWindowSize();

  const findTabByKey = (key, value) => {
    let tab = {};
    headerTabs.forEach((t, index) => {
      if (t[key] === value) {
        tab = t;
        tab.index = index;
        return null;
      }
    });
    return tab;
  };

  const callback = (entries) => {
    entries.forEach((entry) => {
      if (entry.intersectionRatio > 0.7) {
        const tab = findTabByKey('sectionId', entry.target.id);
        setTabIndex(tab.index);
      }
    });
  };

  useFirstRenderSkip(() => {
    if (storePurchaseData?.showStoreTab === false) {
      setShowStoreTab(false);
    }
  }, [storePurchaseData?.showStoreTab]);

  useEffect(() => {
    if (!showStoreTab) {
      const { index } = findTabByKey('id', TABSID.STORE);
      if (index > -1) {
        const tabs = [...headerTabs];
        tabs.splice(index, 1);
        setHeaderTabs(tabs);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showStoreTab]);

  useEffect(() => {
    setTabIndex(findTabByKey('page', pageName).index);
    if (pageName === GOAL_HOME) {
      const options = {
        rootMargin: '0px',
        threshold: 0.7
      };

      setTimeout(() => {
        const observer = new IntersectionObserver(callback, options);
        headerTabs.forEach((tab) => {
          if (tab.sectionId) {
            const section = document.getElementById(tab.sectionId);
            if (section) observer.observe(section);
          }
        });
      }, 5000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageName, headerTabs.length]);

  const handleOnChange = (index, { id: tabId }) => {
    const selectedTab = findTabByKey('id', tabId);
    const section_name = selectedTab.sectionName;
    segment.track(HOME_NAVIGATION_CLICKED, {
      ...goalSegmentData,
      last_primary_source_section: 'Goal Home',
      last_primary_source: pageName,
      navigation_option: section_name
    });

    setTabIndex(index);
    if (tabId === TABSID.SUCCESS_STORIES) {
      const route = {
        href: '/goal/[goalSlug]/[goalUID]/success-story',
        as: `/goal/${goalSlug}/${goalUID}/success-story`
      };
      pushRoute(route.href, route.as);
    } else if (tabId === TABSID.SUBSCRIPTION_PLANS) {
      const route = {
        href: '/goal/[goalSlug]/[goalUID]/subscriptions',
        as: `/goal/${goalSlug}/${goalUID}/subscriptions`
      };
      pushRoute(route.href, route.as);
    } else if (tabId === TABSID.STORE) {
      segment.track(TAB_STORE_CLICKED, {
        ...goalSegmentData
      });
      const route = {
        href: '/goal/[goalSlug]/[goalUID]/store?lps=Free+Home+Page&lpss=Store+Tab',
        as: `/goal/${goalSlug}/${goalUID}/store?lps=Free+Home+Page&lpss=Store+Tab`
      };
      pushRoute(route.href, route.as);
    } else if (pageName === GOAL_HOME) {
      const selectedSection = document.getElementById(selectedTab.sectionId);
      const headerHeight = document.querySelector('header').offsetHeight;
      if (selectedSection) {
        window.scrollTo({
          top: selectedSection.offsetTop - headerHeight - 20,
          left: 0,
          behavior: 'smooth'
        });
        window.location.hash = selectedTab.hash;
      }
    } else {
      const route = {
        href: '/goal/[goalSlug]/[goalUID]',
        as: `/goal/${goalSlug}/${goalUID}#${selectedTab.hash}`
      };
      pushRoute(route.href, route.as);
    }
  };

  const onClose = useCallback(() => {
    closeLoginDrawer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { showStoreTooltip } = useStoreNudge({
    showStoreTab
  });
  const isTabletOrMobile = isTablet || isMobile;

  return (
    <>
      <LoginComponent drawerProps={{ drawerState: showLoginDrawer, onClose }} />
      <HeaderContainer transparent={transparent}>
        <HeaderParent>
          <LogoGoalSelectorWrapper>
            <LogoWrapper>
              <Logo isReload={isReload} disabled={disabled} withoutText />
            </LogoWrapper>
            <GoalSelectorWrapper>
              <GoalSelector />
            </GoalSelectorWrapper>
          </LogoGoalSelectorWrapper>
          <RightContentWrapper>
            <SearchWrapper>
              <NewSearchTab />
            </SearchWrapper>
            <ContactUs />
            <ReferralNudge />
            <LoginProfileWrapper>
              {isLoggedIn ? (
                <HeaderUserContainer>
                  <HeaderUser excludeOptions={['darkMode']} />
                </HeaderUserContainer>
              ) : (
                <RenderLoginAction isJoinForFreeCtaExperiment />
              )}
            </LoginProfileWrapper>
            <ScreenWrapper className="desktop">
              <PaymentNudge />
            </ScreenWrapper>
          </RightContentWrapper>
        </HeaderParent>
        <ScreenWrapper className="mobile">
          <RenderTabs
            currentTabIndex={currentTabIndex}
            handleOnChange={handleOnChange}
            headerTabs={headerTabs}
            showStoreTooltip={isTabletOrMobile && showStoreTooltip}
          />
        </ScreenWrapper>
        <TabContainer>
          <TabSubContainer>
            <ScreenWrapper className="desktop">
              <RenderTabs
                currentTabIndex={currentTabIndex}
                handleOnChange={handleOnChange}
                headerTabs={headerTabs}
                showStoreTooltip={!isTabletOrMobile && showStoreTooltip}
              />
            </ScreenWrapper>
          </TabSubContainer>
        </TabContainer>
      </HeaderContainer>
    </>
  );
};

NewGoalHeader.defaultProps = {
  transparent: false,
  isReload: true,
  disabled: false
};

NewGoalHeader.propTypes = {
  transparent: PropTypes.bool,
  isReload: PropTypes.bool,
  disabled: PropTypes.bool
};
export default NewGoalHeader;
